import type { IErrorApiResponse, ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  restrictApi,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import { usePurchaseContext } from '~/purchase/context';
import type {
  IDestockingPolicyImportError,
  IDestockingPolicyPayload,
  IDestockingPricingPolicy,
  IUpdateDestockingPolicyVariables,
} from '~/purchase/types';

type Options = {
  create?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse<IDestockingPolicyImportError>,
    IDestockingPolicyPayload
  >;
  getById?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IDestockingPricingPolicy>,
      IErrorApiResponse
    >
  > & {
    args: { id: string };
  };
  update?: UseMutationOptions<
    ISuccessApiResponse<IDestockingPricingPolicy>,
    IErrorApiResponse,
    IUpdateDestockingPolicyVariables
  >;
};

export const useManageDestockingPricingPolicies = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { tenantCode } = useConfig();
  const { callCoreApi, pricingPoliciesDomainBaseUrl } = useApi();
  const { state } = usePurchaseContext();
  const idCustomer = state.targetCustomer?.id as string;

  const createMutation = useMutation({
    mutationFn: ({ file, name, customerId, supplierIds, criteria }) => {
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('name', name);
      formData.append('customerId', customerId);
      supplierIds.forEach((supplierId) => {
        formData.append('supplierIds[]', String(supplierId));
      });
      criteria.forEach((criterion, index) => {
        Object.entries(criterion).forEach(([key, value]) => {
          formData.append(`criteria[${index}][${key}]`, value);
        });
      });

      return callCoreApi<ISuccessApiResponse>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/destocking-pricing-policies`,
        { method: 'POST', body: formData, asJson: false },
      );
    },
    keysToInvalidateOnSuccess: (variables) => [
      PurchaseQueryKeys.destockingPricingPolicies.base(variables.customerId),
    ],
    ...options.create,
  });

  const getByIdQuery = useQuery({
    queryKey: PurchaseQueryKeys.destockingPricingPolicies.detail(
      idCustomer,
      options.getById?.args.id as string,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IDestockingPricingPolicy>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/destocking-pricing-policies/${options.getById?.args.id}`,
      ),
    refetchOnWindowFocus: false,
    ...options.getById,
    enabled: Boolean(options.getById?.args.id) && options.getById?.enabled,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }) =>
      callCoreApi<ISuccessApiResponse<IDestockingPricingPolicy>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/destocking-pricing-policies/${id}`,
        { method: 'PUT', body: JSON.stringify(body) },
      ),
    // Both keys are invalidated whatever the body was: closing needs the listing (badge + closing
    // date), editing needs the listing and the detail the edit screen reads back.
    keysToInvalidateOnSuccess: (variables) => [
      PurchaseQueryKeys.destockingPricingPolicies.base(idCustomer),
      PurchaseQueryKeys.destockingPricingPolicies.detail(
        idCustomer,
        variables.id,
      ),
    ],
    ...options.update,
  });

  return restrictApi(options, {
    create: createMutation,
    getById: getByIdQuery,
    update: updateMutation,
  });
};
